import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  ProjectTablesTableStoreState,
  sortDirection,
} from "./ProjectTablesTableProvider.types";
import {
  getSortedAndFilteredTables,
  getSortedAndFilteredTablesInDisplayOrder,
} from "components/ProjectTablesTable/ProjectTablesTable.helpers";
import { captureException } from "@sentry/react";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { EnqueueActionResult } from "pages/gdt/store/queue";
import { ProjectTablesTableColumns } from "components/ProjectTablesTable/ProjectTablesTable";
import { isDefined } from "utils/isDefined";
import { ProjectTablesTableColumn } from "../ProjectTablesTable.types";

type CreateProjectTablesTableStoreOptions = {
  STORAGE_KEY_BASE: string;
  tables: ProjectTablesTableStoreState["tables"];
  folders: ProjectFolderDjango[];
  updateAnalysisTable: (options: {
    tableId: string;
    folder?: string | null;
    hidden?: boolean;
  }) => Promise<EnqueueActionResult<string>>;
  updateDataTable: (params: {
    tableId: string;
    folder?: string | null;
    hidden?: boolean;
  }) => Promise<EnqueueActionResult<string>>;
  updateWorkflowTable: (params: {
    tableId: string;
    folder?: string | null;
    hidden?: boolean;
  }) => Promise<EnqueueActionResult<string>>;
};
export const createProjectTablesTableStore = ({
  STORAGE_KEY_BASE,
  tables,
  folders,
  updateAnalysisTable,
  updateDataTable,
  updateWorkflowTable,
}: CreateProjectTablesTableStoreOptions) => {
  const STORAGE_KEYS = {
    showHidden: `${STORAGE_KEY_BASE}-showHidden`,
    sortedColumn: `${STORAGE_KEY_BASE}-sortedColumn`,
    visibleColumnsMap: `${STORAGE_KEY_BASE}-visibleColumnsMap`,
    foldersExpandedMap: `${STORAGE_KEY_BASE}-foldersExpandedMap`,
  };

  // get initial state persisted in local storage
  const {
    initialShowHidden,
    initialSortedColumn,
    initialVisibleColumnsMap,
    initialExpandedFoldersMap,
  } = getInitialStateFromLocalStorage(STORAGE_KEYS);

  return createStore<ProjectTablesTableStoreState>()(
    immer((set, get) => {
      // Save changes to localStorage
      const saveShowHiddenToLocalStorage = () => {
        const { showHidden } = get();
        localStorage.setItem(
          STORAGE_KEYS.showHidden,
          JSON.stringify(showHidden),
        );
      };

      const saveSortedColumnToLocalStorage = () => {
        const { sortedColumn } = get();
        localStorage.setItem(
          STORAGE_KEYS.sortedColumn,
          JSON.stringify(sortedColumn),
        );
      };

      const saveVisibleColumnsMapToLocalStorage = () => {
        const { visibleColumnsMap } = get();
        localStorage.setItem(
          STORAGE_KEYS.visibleColumnsMap,
          JSON.stringify(visibleColumnsMap),
        );
      };

      const saveFoldersExpandedMapToLocalStorage = () => {
        const { foldersExpandedMap } = get();
        localStorage.setItem(
          STORAGE_KEYS.foldersExpandedMap,
          JSON.stringify(foldersExpandedMap),
        );
      };

      return {
        /* ALL TABLES */
        tables,
        setTables: (tables) => {
          set({ tables });
          get().updateSortedAndFilteredTables();
          // if any tables no longer exist, remove them from the selected table map
          const selectedTableMap = { ...get().selectedTableMap };
          for (const tableId of Object.keys(selectedTableMap)) {
            if (!tables.some((table) => table.id === tableId)) {
              delete selectedTableMap[tableId];
            }
          }
          set({ selectedTableMap });
        },

        /* SORTED AND FILTERED TABLES */
        sortedAndFilteredTables: [],
        updateSortedAndFilteredTables: () => {
          const {
            tables,
            folders,
            searchValue,
            sortedColumn,
            showHidden,
            tableFilter,
          } = get();
          const folderIds = folders.map((folder) => folder.id);
          const sortedAndFilteredTables = getSortedAndFilteredTables(
            tables,
            folderIds,
            searchValue,
            tableFilter,
            sortedColumn,
            showHidden,
          );
          set((state) => {
            state.sortedAndFilteredTables = sortedAndFilteredTables;
          });
        },

        /* SEARCH AND FILTER STATE */
        searchValue: "",
        onSearchChange: (e) => {
          const searchValue = e.target.value;
          set({ searchValue });
          set({ isSearching: searchValue.length > 0 });
          // re-sort on changes
          get().updateSortedAndFilteredTables();
        },
        isSearching: false,

        // show/hide hidden deleteTables
        showHidden: initialShowHidden,
        setShowHidden: (showHidden: boolean) => {
          set({ showHidden });
          saveShowHiddenToLocalStorage();
          // re-sort on changes
          get().updateSortedAndFilteredTables();
        },
        toggleTableHidden: async (table, hidden) => {
          // toggle hidden state of table
          const { error } = await (async () => {
            switch (table.kind) {
              case "data":
                return await updateDataTable({
                  tableId: table.id,
                  hidden: hidden,
                });
              case "analysis":
                return await updateAnalysisTable({
                  tableId: table.id,
                  hidden: hidden,
                });
              case "workflow":
                return await updateWorkflowTable({
                  tableId: table.id,
                  hidden: hidden,
                });
            }
          })();

          return { error };
        },

        tableFilter: undefined,
        onChangeTableTypeFilter: (optionId) => {
          set((state) => {
            state.tableFilter =
              state.tableFilter === optionId ? undefined : optionId;
          });
          get().updateSortedAndFilteredTables();
        },

        /* SELECTION STATE */
        selectedTableMap: {},
        selectTable: (tableId, checked) => {
          // supports EuiCheckbox onChange events by accepting optional checked boolean
          // if checked is undefined, toggle the selection
          const nowSelected = isDefined(checked)
            ? checked
            : !get().selectedTableMap[tableId];
          // update lastTableChecked if not previously selected
          if (nowSelected) {
            set({ lastTableChecked: tableId });
          } else {
            set({ lastTableChecked: null });
          }
          // save state
          set((state) => {
            state.selectedTableMap[tableId] = nowSelected;
          });
        },
        getVisibleSelectedTables: () => {
          // returns a list of tables that are currently selected and visible
          const { sortedAndFilteredTables, selectedTableMap } = get();
          return sortedAndFilteredTables.filter(
            (table) => selectedTableMap[table.id],
          );
        },
        getAnyVisibleTableIsSelected: () => {
          // returns true if any visible table is selected
          const { sortedAndFilteredTables, selectedTableMap } = get();
          return sortedAndFilteredTables.some(
            (table) => selectedTableMap[table.id],
          );
        },

        // tracking last checked table for range selection
        lastTableChecked: null,
        setLastTableChecked: (lastTableChecked) => set({ lastTableChecked }),
        // supports selecting a range of tables
        selectRangeOfTables: (tableId) => {
          const { lastTableChecked, selectedTableMap } = get();
          // if lastTableChecked is null, do nothing
          if (lastTableChecked == null) {
            set({ lastTableChecked: tableId });
            return;
          }
          // if lastTableChecked is not null, select all tables between lastTableChecked and tableId
          const {
            sortedAndFilteredTables,
            folders,
            foldersExpandedMap,
            sortedColumn,
          } = get();
          // need to get a list of tables in the order they appear to the user
          const tablesInDisplayOrder = getSortedAndFilteredTablesInDisplayOrder(
            sortedAndFilteredTables,
            folders,
            foldersExpandedMap,
            sortedColumn,
          );

          // check all tables between lastTableChecked and tableId
          const lastTableIndex = tablesInDisplayOrder.findIndex(
            (table) => table.id === lastTableChecked,
          );
          const currentTableIndex = tablesInDisplayOrder.findIndex(
            (table) => table.id === tableId,
          );
          // if either table is not found, do nothing
          if (lastTableIndex === -1 || currentTableIndex === -1) {
            return;
          }
          const startIndex = Math.min(lastTableIndex, currentTableIndex);
          const endIndex = Math.max(lastTableIndex, currentTableIndex);
          const newSelectedTableMap = { ...selectedTableMap };
          for (let i = startIndex; i <= endIndex; i++) {
            const table = tablesInDisplayOrder[i];
            newSelectedTableMap[table.id] = true;
          }
          set({ selectedTableMap: newSelectedTableMap });
          // update lastTableChecked
          set({ lastTableChecked: tableId });
        },

        /* SORTING AND COLUMN VISIBLITY */
        visibleColumnsMap: isDefined(initialVisibleColumnsMap)
          ? initialVisibleColumnsMap
          : ProjectTablesTableColumns.reduce(
              (acc, column) => {
                acc[column.id] = isDefined(column.defaultVisibility)
                  ? column.defaultVisibility
                  : true;
                return acc;
              },
              {} as Record<string, boolean>,
            ),
        toggleVisibleColumn: (columnId) => {
          set((state) => {
            state.visibleColumnsMap[columnId] =
              !state.visibleColumnsMap[columnId];
          });
          saveVisibleColumnsMapToLocalStorage();
        },
        setVisibleColumnsMap: (visibleColumnsMap) => {
          set({ visibleColumnsMap });
          saveVisibleColumnsMapToLocalStorage();
        },

        sortedColumn: initialSortedColumn ?? ["name", "ASC"],
        setSortedColumn: (sortedColumn) => {
          set({ sortedColumn });
          saveSortedColumnToLocalStorage();
          // re-sort tables
          get().updateSortedAndFilteredTables();
        },

        onColumnSort: (columnId) => {
          set((state) => {
            // toggle direction if already sorted by this column
            // otherwise, sort by this column in ascending order
            const [currentColumn, currentDirection] = state.sortedColumn;
            state.sortedColumn =
              currentColumn === columnId && currentDirection === "ASC"
                ? [columnId, "DESC"]
                : [columnId, "ASC"];
          });
          saveSortedColumnToLocalStorage();
          // re-sort tables
          get().updateSortedAndFilteredTables();
        },

        /* MODALS */
        visibleModal: null,
        setVisibleModal: (visibleModal) => set({ visibleModal }),
        onCloseModal: () =>
          set({
            visibleModal: null,
            editingTable: null,
            editingFolder: null,
          }),

        editingTable: null,
        setEditingTable: (editingTable) => set({ editingTable }),

        openCreateDataTableModal: (folder) => {
          set({
            visibleModal: "createData",
            editingTable: null,
            editingFolder: folder,
          });
        },
        openCreateAnalysisTableModal: (folder) => {
          set({
            visibleModal: "createAnalysis",
            editingTable: null,
            editingFolder: folder,
          });
        },
        openEditTableModal: (table) => {
          set({
            visibleModal: (() => {
              switch (table.kind) {
                case "data":
                  return "editData";
                case "analysis":
                  return "editAnalysis";
                case "workflow":
                  return "editWorkflow";
              }
            })(),
            editingTable: table,
          });
        },
        openDeleteTableModal: (table) => {
          set({
            visibleModal: "deleteTable",
            editingTable: table,
          });
        },
        openDeleteTablesModal: () => {
          set({
            visibleModal: "deleteTables",
          });
        },

        editingFolder: null,
        setEditingFolder: (editingFolder) => set({ editingFolder }),

        openCreateFolderModal: () => {
          set({ visibleModal: "createFolder", editingFolder: null });
        },
        openEditFolderModal: (folder) => {
          set({
            visibleModal: "editFolder",
            editingFolder: folder,
          });
        },
        openDeleteFolderModal: (folder) => {
          set({
            visibleModal: "deleteFolder",
            editingFolder: folder,
          });
        },

        /* FOLDER STATE */
        folders,
        foldersExpandedMap: isDefined(initialExpandedFoldersMap)
          ? initialExpandedFoldersMap
          : {},
        toggleFolderExpanded: (folderId) => {
          // is folder expanded or not
          // not in map is assumed to be expanded
          const isExpanded = isDefined(get().foldersExpandedMap[folderId])
            ? get().foldersExpandedMap[folderId]
            : true;
          // if it is expanded, collapse it
          // if it is not expanded, expand it
          set((state) => {
            state.foldersExpandedMap[folderId] = !isExpanded;
          });
          saveFoldersExpandedMapToLocalStorage();
          // deselect any tables that are in a newly collapsed folder
          if (isExpanded) {
            const tablesInFolder = get().tables.filter(
              (table) => table.folder === folderId,
            );
            const newSelectedTableMap = { ...get().selectedTableMap };
            for (const table of tablesInFolder) {
              newSelectedTableMap[table.id] = false;
            }
            set({ selectedTableMap: newSelectedTableMap });
          }
        },

        moveSelectedToFolder: (folderId) => {
          const visibleSelectedTables = get().getVisibleSelectedTables();
          for (const table of visibleSelectedTables) {
            switch (table.kind) {
              case "data":
                void updateDataTable({
                  tableId: table.id,
                  folder: folderId,
                });
                break;
              case "analysis":
                void updateAnalysisTable({
                  tableId: table.id,
                  folder: folderId,
                });
                break;

              case "workflow":
                void updateWorkflowTable({
                  tableId: table.id,
                  folder: folderId,
                });
                break;
            }
          }
          set((state) => ({
            selectedTableMap: {
              ...state.selectedTableMap,
              ...Object.fromEntries(
                visibleSelectedTables.map(
                  (table) => [table.id, false] as const,
                ),
              ),
            },
          }));
        },

        moveSelectedOutOfFolder: () => {
          const visibleSelectedTables = get().getVisibleSelectedTables();
          for (const table of visibleSelectedTables) {
            switch (table.kind) {
              case "data":
                void updateDataTable({
                  tableId: table.id,
                  folder: null,
                });
                break;
              case "analysis":
                void updateAnalysisTable({
                  tableId: table.id,
                  folder: null,
                });
                break;

              case "workflow":
                void updateWorkflowTable({
                  tableId: table.id,
                  folder: null,
                });
                break;
            }
          }
          set((state) => ({
            selectedTableMap: {
              ...state.selectedTableMap,
              ...Object.fromEntries(
                visibleSelectedTables.map(
                  (table) => [table.id, false] as const,
                ),
              ),
            },
          }));
        },
        moveTableToFolder: (table, folderId) => {
          switch (table.kind) {
            case "data":
              void updateDataTable({
                tableId: table.id,
                folder: folderId,
              });
              break;
            case "analysis":
              void updateAnalysisTable({
                tableId: table.id,
                folder: folderId,
              });
              break;

            case "workflow":
              void updateWorkflowTable({
                tableId: table.id,
                folder: folderId,
              });
              break;
          }
        },

        /* MENU POPOVERS */
        popoverId: null,
        togglePopover: (id) => {
          set((state) => {
            state.popoverId = state.popoverId === id ? null : id;
          });
        },
        closePopover: () => {
          set({ popoverId: null });
        },
      };
    }),
  );
};

/**
 * Retrieves saved state for provider from localStorage
 * This function attempts to parse and validate the stored data for sorted column, visible columns map,
 * and folders expanded map. If parsing fails or the data is invalid, the corresponding state will be undefined
 * and an error will be logged to Sentry.
 *
 * @param STORAGE_KEYS - the keys used to retrieve data from localStorage:
 *
 * @returns An object containing the initial state:
 */
const getInitialStateFromLocalStorage = (STORAGE_KEYS: {
  showHidden: string;
  sortedColumn: string;
  visibleColumnsMap: string;
  foldersExpandedMap: string;
}) => {
  let initialShowHidden = false;
  let initialSortedColumn:
    | [ProjectTablesTableColumn["id"], sortDirection]
    | undefined;
  let initialVisibleColumnsMap: Record<string, boolean> | undefined;
  let initialExpandedFoldersMap: Record<string, boolean> | undefined;
  const showHidden = localStorage.getItem(STORAGE_KEYS.showHidden);
  const sortedColumn = localStorage.getItem(STORAGE_KEYS.sortedColumn);
  const visibleColumnsMap = localStorage.getItem(
    STORAGE_KEYS.visibleColumnsMap,
  );
  const foldersExpandedMap = localStorage.getItem(
    STORAGE_KEYS.foldersExpandedMap,
  );

  if (showHidden != null) {
    try {
      const parsed: unknown = JSON.parse(showHidden);
      if (typeof parsed === "boolean") {
        initialShowHidden = parsed;
      }
    } catch (error) {
      captureException("Error parsing show hidden from localStorage", {
        extra: {
          error: error,
        },
      });
    }
  }

  if (sortedColumn != null) {
    try {
      const parsed: unknown = JSON.parse(sortedColumn);
      if (
        Array.isArray(parsed) &&
        parsed.length === 2 &&
        typeof parsed[0] === "string" &&
        (parsed[1] === "ASC" || parsed[1] === "DESC")
      ) {
        initialSortedColumn = parsed as [
          ProjectTablesTableColumn["id"],
          sortDirection,
        ];
      }
    } catch (error) {
      captureException("Error parsing sorted column from localStorage", {
        extra: {
          error: error,
        },
      });
    }
  }

  if (visibleColumnsMap != null) {
    try {
      const parsed: unknown = JSON.parse(visibleColumnsMap);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed) &&
        Object.values(parsed).every((value) => typeof value === "boolean")
      ) {
        initialVisibleColumnsMap = parsed as Record<string, boolean>;
      }
    } catch (error) {
      captureException("Error parsing column visibility from localStorage", {
        extra: {
          error: error,
        },
      });
    }
  }

  if (foldersExpandedMap != null) {
    try {
      const parsed: unknown = JSON.parse(foldersExpandedMap);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed) &&
        Object.values(parsed).every((value) => typeof value === "boolean")
      ) {
        initialExpandedFoldersMap = parsed as Record<string, boolean>;
      }
    } catch (error) {
      captureException("Error parsing folders expanded map from localStorage", {
        extra: {
          error: error,
        },
      });
    }
  }
  return {
    initialShowHidden,
    initialSortedColumn,
    initialVisibleColumnsMap,
    initialExpandedFoldersMap,
  };
};
